import {
    Alert,
    Grid2
} from '@mui/material';
import { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';

import { ValidatorForm } from 'react-material-ui-form-validator';
import { StandardButton } from '../../../_components/button/primary.button';
import { StandardPanel } from '../../../_components/panel/standard.panel';
import { StandardTextValidator } from '../../../_components/textValidator/standardTextValidator';
import { toastr } from '../../../_components/toastr/toastr';
import { handleErrorResponse } from '../../../_helpers/handleResponse.helper';
import { catalogueItemUserBidService } from '../../../_services/catalogueItemBidService.service';
import AuctionUsersDropdown from './auction-usersDropdown';
import { IDropdownData } from '../../../_common/interface/dropdown/iDropdownData.interface';
import { ActualAuctionStatusEnum } from '../../../_common/enum/actualAuctionStatus';
import { isOnBehalfBidPaused } from '../../../_helpers/auction.helper';
import { MessageNotification } from '../../../_components/box/message-notification';
import { AuctionPostbackStatusEnum } from '../../../_common/enum/auctionPostbackStatus.enum';
import { accountService } from '../../../_services/accounts.service';
import { CatalogueItemUserBidStatusEnum } from '../../../_common/enum/catalogueItemUserBidStatusEnum';
import { CatalogueItemBidHistoryBox } from './catalogue-item-bid-history-box';


export function AuctionCatalogueItemAdminUserBid(params: {
    catalogueItem?: any,
    auctionId?: number,
    actualAuctionStatusId: ActualAuctionStatusEnum,
    catalogueItemLoading: boolean,
    setProxyBidderId?: Dispatch<SetStateAction<string>>
}) {

    const [saving, setSaving] = useState(false);
    const [bid, setBid] = useState<string>("0");
    const [submittedBid, setSubmittedBid] = useState<string>("0");
    const [placedBids, setPlacedBids] = useState<string[]>([]);
    const lastItemIdRef = useRef<number | null>(null);
    const isSubmittingRef = useRef(false);

    const [catalogueUser, setAuctionUser] = useState<IDropdownData | null>(null);

    const BID_MIN = 0;
    const BID_MAX = 999;

    function clampBid(value: string | number | null | undefined): string {
        const n = parseFloat(String(value ?? "0"));
        if (Number.isNaN(n)) return "0.00";
        const clamped = Math.min(BID_MAX, Math.max(BID_MIN, n));
        return clamped.toFixed(2);
    }

    useEffect(() => {
        if (!params.catalogueItem) return;

        const itemId = params.catalogueItem?.id ?? null;
        if (itemId !== lastItemIdRef.current) {
            lastItemIdRef.current = itemId;
            setBid("0");
            setSubmittedBid(clampBid(params.catalogueItem?.catalogueItemBid?.bid ?? "0"));
            setPlacedBids([]);
            return;
        }

        setSubmittedBid(clampBid(params.catalogueItem?.catalogueItemBid?.bid ?? "0"));
    }, [params.catalogueItem]);

    useEffect(() => {
        if (params.setProxyBidderId)
            params.setProxyBidderId((catalogueUser as any)?.userId ?? "")
    }, [catalogueUser]);

    const bidPaused = isOnBehalfBidPaused(params.actualAuctionStatusId);
    const showBidHistory = accountService.isProxyBidder || accountService.isAdmin;
    const reofferedToSelectedUser =
        params.actualAuctionStatusId === ActualAuctionStatusEnum.ReOffered &&
        (params.catalogueItem?.catalogueItemBid?.catalogueItemUserBidStatusId === CatalogueItemUserBidStatusEnum.ReOffered ||
            params.catalogueItem?.auctionPostbackStatusId === AuctionPostbackStatusEnum.ReOffered);

    function handleChange(event: any) {
        const nextBid = event.target?.value ?? "0";
        setBid(nextBid);

        // Record every keystroke (best-effort) so it appears in the audit report,
        // tied to the selected user via catalogueUserId. Only log once a user is selected.
        if (!params.catalogueItem?.id || !catalogueUser?.id || isSubmittingRef.current) {
            return;
        }

        catalogueItemUserBidService.typeBid(params.catalogueItem.id, nextBid, catalogueUser?.id).catch(() => { });
    }

    function updateDecimals() {
        setBid(clampBid(bid));
    }

    function submit() {
        isSubmittingRef.current = true;
        setSaving(true);
        const clampedBid = clampBid(bid);
        setBid(clampedBid);
        return catalogueItemUserBidService.post(params.catalogueItem.id, clampedBid, catalogueUser?.id)
            .then(
                () => {
                    setSubmittedBid(clampedBid);
                    setPlacedBids((prev) => [clampedBid, ...prev]);
                    toastr.success("Bid placed");
                }, handleErrorResponse)
            .finally(() => {
                isSubmittingRef.current = false;
                setSaving(false);
            });
    }

    return (
        <>
        <Grid2 size={{ xs: 12 }} sx={{ '& .MuiCard-root': { margin: 0 } }}>
            <StandardPanel title={!!params.catalogueItem ? `Enter bid on behalf of a user` : !!params.catalogueItemLoading ? "Loading.." : undefined} loading={!!params.catalogueItemLoading}>

                {(!params.catalogueItemLoading && !params.catalogueItem) &&
                    <MessageNotification message='No active catalogue item selected' />
                }

                {(!params.catalogueItemLoading && !!params.catalogueItem) &&
                    <ValidatorForm
                        onSubmit={submit}
                        noValidate>

                        <Grid2 container spacing={{ xs: 0.5, sm: 1 }} sx={{ padding: { xs: '4px', sm: '8px' } }}>

                            <Grid2 size={{ xs: 12 }}>

                                {reofferedToSelectedUser && accountService.isProxyBidder &&
                                    <Alert elevation={1} severity="info" ><strong>Tied Bid</strong> - please place another bid</Alert>
                                }
                                {reofferedToSelectedUser && !accountService.isProxyBidder &&
                                    <Alert elevation={1} severity="info" >Catalogue Item Auction has been <strong>ReOffered</strong></Alert>
                                }

                            </Grid2>
                            <Grid2 size={{ xs: 12, sm: 2 }}>
                                <AuctionUsersDropdown
                                    style={{ "height": "100%", width: "100%" }}
                                    catalogueUser={catalogueUser}
                                    setAuctionUser={setAuctionUser}
                                    catalogueId={params.catalogueItem?.catalogueId} onChange={(d) => { }}
                                />
                            </Grid2>

                            <Grid2 size={{ xs: 12, sm: 10 }}>
                                <StandardTextValidator
                                    name="bid"
                                    label="Your Bid"
                                    fullWidth
                                    type="number"
                                    value={bid}
                                    step="0.01"
                                    inputProps={{ min: BID_MIN, max: BID_MAX }}
                                    disabled={bidPaused}
                                    reuired
                                    validators={['required', 'minNumber:0', 'maxNumber:999']}
                                    errorMessages={['required field', 'Minimum is 0', 'Maximum is 999']}
                                    onBlur={updateDecimals}
                                    onChange={handleChange}
                                />
                            </Grid2>


                            <Grid2 size={{ xs: 12 }}>
                                <Grid2 container spacing={{ xs: 0.5, sm: 1 }}>
                                    <Grid2 size={{ xs: 12 }}>
                                        <StandardButton
                                            saving={saving}
                                            loading={params.catalogueItemLoading}
                                            disabled={bidPaused || !catalogueUser}
                                            icon="check"
                                            variant='success'
                                            type="submit">
                                            Submit your bid
                                        </StandardButton>
                                    </Grid2>
                                </Grid2>
                            </Grid2>
                        </Grid2>

                    </ValidatorForm>
                }
            </StandardPanel>
        </Grid2>
        {showBidHistory && !!params.catalogueItem &&
            <CatalogueItemBidHistoryBox submittedBid={submittedBid} placedBids={placedBids} />
        }
        </>
    )
}
